import invariant from 'invariant';
import {
  BiRawArrayType,
  BiDimensionFieldMeta,
  BiFieldMeta,
  BiFieldMetaToPrimitiveType,
  BiMeasureMeta,
  BiMetadata,
  BiNumberFieldMeta,
  BiRawSingularType,
  BiQueryComponents,
  BiDynamicNormalizedMeasure,
  BiRawValueType,
  BiStringFieldMeta,
  BiDynamicNormalizedMeasureWithMeta,
  BiNormalizedMeasure,
  BiQueryFilter,
  BiMeasureFieldMeta,
} from './types';
import {
  formatNumber,
  formatPercentageSmart,
  smartLowerCase,
  smartSentenceCase,
} from '../utils/helpers';
import assertNever from '../utils/assertNever';
import { NONE_GROUP_NAME } from '../analysis/drilldownConstants';
import {
  GQBiDimensionListOptionItem,
  GQBiDisplayValueMapping,
  GQBiEmptyStringType,
} from '../generated/graphql';
import objectHash from 'object-hash';
import { DistributiveOmit, RequireSomeProperties } from '../utils/utilTypes';
import { UnexpectedError } from '../errors/UnexpectedError';
import { captureException } from '../utils/sentry';
import {
  getFormattedUnitStringOrNull,
  isNullUnit,
  parseUnitString,
  reformatUnit,
} from '../unit';
import { AGGREGATE_KIND_DISPLAY_VALUE } from './supportedMeasures';
import { DrilldownFilterExpressionPrimitive } from '../drilldown/filterExpressionSchema';
import isNotNullish from '../utils/isNotNullish';
import { condenseMonetaryValue } from '../utils/currencyUtils';
import { Columnar, Row } from '../columnar';

export interface MeasureValueGetterProps<
  RowType extends Record<string, any> = Record<string, BiRawValueType>,
> {
  row: RowType;
  fieldMeta: BiFieldMeta;
}

export interface IntensityMetadata {
  numerator: number;
  denominator: number;
}

export interface BiCalculatedValue<T extends BiRawValueType = BiRawValueType> {
  value: T;
  metadata: {
    intensity?: IntensityMetadata;
  };
}

export type NumericValueWithMetadata = BiCalculatedValue<number>;

function getTypedValue<M extends BiFieldMeta>({
  value,
  fieldMeta,
}: {
  value: NonNullable<BiRawValueType>;
  fieldMeta: M;
}): NonNullable<BiFieldMetaToPrimitiveType<M>>;
function getTypedValue<M extends BiFieldMeta>({
  value,
  fieldMeta,
}: {
  value: BiRawValueType;
  fieldMeta: M;
}): BiFieldMetaToPrimitiveType<M>;
function getTypedValue<M extends BiFieldMeta>({
  value,
  fieldMeta,
}: {
  value: BiRawValueType;
  fieldMeta: M;
}): BiRawValueType {
  if (value == null) {
    return value;
  }

  assertValueMatchesMultiValueSetting({ value, fieldMeta });

  if (Array.isArray(value)) {
    return value.map((v) =>
      getTypedValueSingular({
        value: v,
        fieldMeta: fieldMeta as BiFieldMetaSingular,
      })
    ) as BiRawArrayType;
  }

  return getTypedValueSingular({
    value,
    fieldMeta: fieldMeta as BiFieldMetaSingular,
  });
}

/**
 * asserts the raw value type based on field meta for a non multi value field
 * Note: hopefully it's obvious from the name but isMultiValue should not be used in this function.
 * For performance reasons we don't change it to isMultiValue:false in the callsites but since the type
 * omits it, it should be impossible to use the potentially incorrect value
 * please don't add it back to the type
 */
function getTypedValueSingular<M extends BiFieldMetaSingular>({
  value,
  fieldMeta,
}: {
  value: NonNullable<BiRawValueType>;
  fieldMeta: M;
}): NonNullable<BiFieldMetaToPrimitiveType<M>>;
function getTypedValueSingular<M extends BiFieldMetaSingular>({
  value,
  fieldMeta,
}: {
  value: BiRawValueType;
  fieldMeta: M;
}): BiFieldMetaToPrimitiveType<M>;
function getTypedValueSingular<M extends BiFieldMetaSingular>({
  value,
  fieldMeta,
}: {
  value: BiRawValueType;
  fieldMeta: M;
}): BiRawValueType {
  if (value == null) {
    return value;
  }

  switch (fieldMeta.type) {
    case 'string':
    case 'number':
    case 'boolean':
      invariant(
        typeof value === fieldMeta.type,
        `Expected ${fieldMeta.type} value but got ${typeof value} for field ${
          fieldMeta.fieldId
        }`
      );
      break;
    case 'date':
      invariant(typeof value === 'number', 'Expected timestamp value');
  }
  return value;
}

export const _getTypedValueForTestsDONOTUSE = getTypedValue;

function getTypedValueFromRow({ row, fieldMeta }: MeasureValueGetterProps) {
  const value = row[fieldMeta.fieldId];
  return getTypedValue({ value, fieldMeta });
}

export function getMeasureValue(
  props: MeasureValueGetterProps
): NumericValueWithMetadata {
  // TODO: we ideally shouldn't need this cast?
  const value = getTypedValueFromRow(props) as number;
  return {
    value,
    metadata: {},
  };
}

export interface ValueWithFieldMeta<M extends BiFieldMeta = BiFieldMeta> {
  value: BiFieldMetaToPrimitiveType<M> | null | undefined;
  fieldMeta: M;
}

// some fields aren't configured to be sentence cased, but these strings still will be
const STRINGS_TO_ALWAYS_SENTENCE_CASE = new Set(['unknown']);

function getEmptyStringDisplay(fieldMeta: BiFieldMeta): string {
  switch (fieldMeta.emptyStringType) {
    case GQBiEmptyStringType.Emdash:
      return '—';
    case GQBiEmptyStringType.UnspecifiedWithDisplayName:
      return `Unspecified ${smartLowerCase(fieldMeta.displayName)}`;
    case GQBiEmptyStringType.Empty:
    default:
      return '';
  }
}

function maybeMapDisplayValueWithKnownMapping(
  value: string,
  fieldMeta: BiStringFieldMeta
): string {
  return (
    fieldMeta.staticListOptions?.find((lo) => lo.value === value)?.label ??
    value
  );
}

export interface BiFormatProps<M extends BiFieldMeta = BiFieldMeta> {
  calculatedValue: BiCalculatedValue<BiFieldMetaToPrimitiveType<M>> | undefined;
  fieldMeta: M;
  // make this required so callers must explicitly pass empty
  // (and are then aware this isn't happening if they don't pass it)
  displayValueMappings: Array<GQBiDimensionListOptionItem>;
}

type BiFieldMetaSingular = DistributiveOmit<BiFieldMeta, 'isMultiValue'> & {
  isMultiValue: undefined;
};

export function isMeasureValidForDimensions(
  unitDimension: string | undefined,
  aggregatedUnitDimension: string | undefined,
  queriedDimensions: Array<string>
): boolean {
  if (aggregatedUnitDimension === undefined) {
    return true;
  }

  return (
    (unitDimension !== undefined &&
      queriedDimensions.includes(unitDimension)) ||
    (aggregatedUnitDimension !== undefined &&
      queriedDimensions.includes(aggregatedUnitDimension))
  );
}

export function dependentDimensionsForMeasures(
  measures: Array<BiNormalizedMeasure>,
  dimensions: Array<string>
): Array<string> {
  return measures.flatMap((measure) => {
    const fieldMeta = measure.measureMeta.fieldMeta;
    return requiredDimensionsForMeasure(
      fieldMeta.unitDimension,
      fieldMeta.aggregatedUnitDimension,
      dimensions
    );
  });
}

export function requiredDimensionsForMeasure(
  unitDimension: string | undefined,
  aggregatedUnitDimension: string | undefined,
  queriedDimensions: Array<string>
): Array<string> {
  if (unitDimension && queriedDimensions.includes(unitDimension)) {
    return [];
  }
  if (
    aggregatedUnitDimension &&
    !queriedDimensions.includes(aggregatedUnitDimension)
  ) {
    return [aggregatedUnitDimension];
  } else {
    return [];
  }
}

// If the overall query is selecting the unit columns,
// we shouldn't issue a subquery at levels that don't include that unit column
export function shouldQueryMeasureWithDimensions(
  queryDimensions: Array<string>,
  unitDimension: string,
  subQueryDimensions: Array<string>
): boolean {
  return (
    !queryDimensions.includes(unitDimension) ||
    subQueryDimensions.includes(unitDimension)
  );
}

// note: the overload lets us specify the type more from the outside without losing all constraint on the inside
export function formatWithFieldMeta<M extends BiFieldMeta>(
  props: BiFormatProps<M>
): string;
export function formatWithFieldMeta({
  calculatedValue: { value, metadata } = {
    value: null,
    metadata: {},
  },
  fieldMeta,
  displayValueMappings,
}: BiFormatProps): string {
  const singleFormat = (val: BiRawSingularType) =>
    formatWithFieldMetaSingular({
      calculatedValue: { value: val, metadata },
      fieldMeta: fieldMeta as BiFieldMetaSingular,
      displayValueMappings,
    });

  const result = Array.isArray(value)
    ? value.map(singleFormat).join(', ')
    : singleFormat(value);
  return result;
}

// precomputing this object to avoid creating more garbage during lots format
const DISABLE_COMMAS_FORMAT_OPTIONS = {
  disableCommas: true,
} as const;

/**
 * Note: hopefully it's obvious from the name but isMultiValue should not be used in this function.
 * For performance reasons we don't change it to isMultiValue:false in the callsites but since the type
 * omits it, it should be impossible to use the potentially incorrect value
 * please don't add it back to the type
 */

function formatWithFieldMetaSingular({
  calculatedValue: { value: initialValue } = {
    value: null,
    metadata: {},
  },
  fieldMeta,
  displayValueMappings,
}: {
  calculatedValue:
    | BiCalculatedValue<BiFieldMetaToPrimitiveType<BiFieldMetaSingular>>
    | undefined;
  fieldMeta: BiFieldMetaSingular;
  // make this required so callers must explicitly pass empty
  // (and are then aware this isn't happening if they don't pass it)
  displayValueMappings: Array<GQBiDimensionListOptionItem>;
}): string {
  const formattedValue = (() => {
    const possiblyMappedValue = displayValueMappings.find(
      (mapping) => mapping.value === initialValue
    );
    if (possiblyMappedValue?.label) {
      return possiblyMappedValue?.label;
    }

    const value = standardizeRawFieldValueSingular({
      value: initialValue,
      fieldMeta,
    });

    // note "" is translated to null in the standardize step
    if (value == null) {
      return getEmptyStringDisplay(fieldMeta);
    }
    switch (fieldMeta.type) {
      case 'string': {
        const typedValue = maybeMapDisplayValueWithKnownMapping(
          getTypedValueSingular({ value, fieldMeta }),
          fieldMeta
        );
        return fieldMeta.shouldSentenceCaseValues ||
          STRINGS_TO_ALWAYS_SENTENCE_CASE.has(typedValue)
          ? smartSentenceCase(typedValue)
          : typedValue;
      }
      case 'number':
        const typedValue = getTypedValueSingular({ value, fieldMeta });

        const suffix = fieldMeta.includeUnit
          ? ` ${
              getUserReadableUnitFromFieldMetaIfPossible(fieldMeta) ??
              fieldMeta.unit
            }`
          : '';

        const disableCommas = fieldMeta.disableCommas
          ? DISABLE_COMMAS_FORMAT_OPTIONS
          : undefined;
        const formattedValue = fieldMeta.shouldFormatToPercent
          ? formatPercentageSmart(typedValue, {
              fixedRoundingPlaces: fieldMeta.precision,
              includeCommaSeparators: !disableCommas,
            })
          : formatNumber(typedValue, fieldMeta.precision, disableCommas);

        return `${formattedValue}${suffix}`;
      case 'date':
        const jsTimestamp = getTypedValueSingular({ value, fieldMeta });
        return new Intl.DateTimeFormat(
          undefined,
          fieldMeta.format ?? {}
        ).format(new Date(jsTimestamp));
      case 'boolean':
        return getTypedValueSingular({ value, fieldMeta }).toLocaleString();
      default:
        assertNever(fieldMeta);
    }
  })();
  return formattedValue;
}

function isBiStringValueNonNull(
  value: string | null | undefined
): value is NonNullable<typeof value> {
  return !(value === null || value === undefined || value === '');
}

export function isBiStringValueNonPseudoNull(
  value: string | null | undefined
): value is NonNullable<typeof value> {
  return !(
    !isBiStringValueNonNull(value) ||
    value === 'null' ||
    value === NONE_GROUP_NAME
  );
}

/**
 * @deprecated please don't add things here, we want to do 0 client side standardization ultimately
 */
// note: the overload lets us specify the type more from the outside without losing all constraint on the inside
export function standardizeRawFieldValue<M extends BiFieldMeta>({
  value,
  fieldMeta,
}: ValueWithFieldMeta<M>): BiFieldMetaToPrimitiveType<M>;
export function standardizeRawFieldValue(
  args: ValueWithFieldMeta
): BiRawValueType {
  const { value, fieldMeta } = args;
  assertValueMatchesMultiValueSetting(args);

  const standardizeSingular = (val: BiRawSingularType) =>
    standardizeRawFieldValueSingular({
      value: val,
      fieldMeta: fieldMeta as BiFieldMetaSingular,
    });

  return Array.isArray(value)
    ? (value.map(standardizeSingular) as BiRawArrayType)
    : standardizeSingular(value);
}

function assertValueMatchesMultiValueSetting({
  value,
  fieldMeta,
}: {
  value: BiRawValueType;
  fieldMeta: BiFieldMeta;
}) {
  const isValueArray = Array.isArray(value);
  UnexpectedError.invariant(
    value == null || isValueArray === !!fieldMeta.isMultiValue,
    `Value isMultiValue didn't match whether it was an array`,
    {
      data: {
        isValueArray,
        isMultiValue: fieldMeta.isMultiValue,
        fieldId: fieldMeta.fieldId,
      },
    }
  );
}

/**
 * Note: hopefully it's obvious from the name but isMultiValue should not be used in this function.
 * For performance reasons we don't change it to isMultiValue:false in the callsites but since the type
 * omits it, it should be impossible to use the potentially incorrect value
 * please don't add it back to the type
 */

function standardizeRawFieldValueSingular<M extends BiFieldMetaSingular>({
  value,
  fieldMeta,
}: {
  value: BiFieldMetaToPrimitiveType<M>;
  fieldMeta: M;
}): NonNullable<BiFieldMetaToPrimitiveType<M>>;
function standardizeRawFieldValueSingular({
  value,
  fieldMeta,
}: {
  value: BiFieldMetaToPrimitiveType<BiFieldMetaSingular> | null | undefined;
  fieldMeta: BiFieldMetaSingular;
}): BiRawSingularType;
function standardizeRawFieldValueSingular({
  value,
  fieldMeta,
}: {
  value: BiFieldMetaToPrimitiveType<BiFieldMetaSingular> | null | undefined;
  fieldMeta: BiFieldMetaSingular;
}): BiRawSingularType {
  switch (fieldMeta.type) {
    case 'string': {
      const typedValue = getTypedValueSingular({ value, fieldMeta })?.trim();
      const isNull = fieldMeta.shouldNullifyPseudoNulls
        ? !isBiStringValueNonPseudoNull(typedValue)
        : !isBiStringValueNonNull(typedValue);
      return isNull ? null : typedValue;
    }
    case 'number':
      return getTypedValueSingular({ value, fieldMeta });
    case 'date':
      return getTypedValueSingular({ value, fieldMeta });
    case 'boolean':
      return getTypedValueSingular({ value, fieldMeta });
    default:
      assertNever(fieldMeta);
  }
}

export function getCalculatedDimensionValueFromRaw(props: {
  value: BiFieldMetaToPrimitiveType<BiDimensionFieldMeta>;
  fieldMeta: BiDimensionFieldMeta;
}): BiCalculatedValue<BiFieldMetaToPrimitiveType<BiDimensionFieldMeta>> {
  const value = standardizeRawFieldValue(props);
  return {
    value,
    metadata: {},
  };
}

export function formatRawDimensionValue({
  value,
  fieldMeta,
}: {
  value: string | null | undefined;
  fieldMeta: BiDimensionFieldMeta;
}): string {
  return formatWithFieldMeta({
    calculatedValue: getCalculatedDimensionValueFromRaw({
      value,
      fieldMeta,
    }),
    fieldMeta,
    displayValueMappings: [],
  });
}

export function makeMeasuresWithDenominator(
  numerator: BiMeasureMeta,
  denominator: BiMeasureMeta
): Array<BiDynamicNormalizedMeasure> {
  const baseMeasure = numerator.fieldMeta.fieldId;
  if (baseMeasure === denominator.fieldMeta.fieldId) {
    return [
      // this is percent of total
      {
        baseMeasure,
        normalizingQuery: {
          measure: baseMeasure,
          dimensions: [],
          filters: [],
        },
      },
      // this is percent of current view
      {
        baseMeasure,
        normalizingQuery: {
          measure: baseMeasure,
          computedFiltersType: 'NumeratorIntersection',
          dimensions: [],
        },
      },
    ];
  }
  return [
    // this is measure over measure with the largest set of common selected dimensions
    // it handles the legacy intencity case in case 'month' is selected
    {
      baseMeasure,
      normalizingQuery: {
        measure: denominator.fieldMeta.fieldId,
        computedDimensionType: 'NumeratorIntersection',
        filters: [],
      },
    },
  ];
}

export function addMetaToDynamicNormalizedMeasure(
  rawNormalizedMeasure: BiDynamicNormalizedMeasure,
  biMetadata: BiMetadata
): BiDynamicNormalizedMeasureWithMeta {
  return {
    ...rawNormalizedMeasure,
    measureMeta: computeNormalizedMeasureMeta(rawNormalizedMeasure, biMetadata),
  };
}

function computeNormalizedMeasureRawField({
  baseMeasure,
  normalizingQuery,
}: BiDynamicNormalizedMeasure) {
  return `${baseMeasure}${normalizingQuery?.measure ?? ''}_${objectHash(
    normalizingQuery ?? null,
    { respectType: false }
  )}`;
}

export function computeNormalizedMeasureMeta(
  rawNormalizedMeasure: BiDynamicNormalizedMeasure,
  biMetadata: BiMetadata
): BiMeasureMeta {
  const baseMeasureMeta = findMeasureMeta(
    rawNormalizedMeasure.baseMeasure,
    biMetadata
  );
  const normalizingMeasureMeta = rawNormalizedMeasure?.normalizingQuery?.measure
    ? findMeasureMeta(rawNormalizedMeasure.normalizingQuery.measure, biMetadata)
    : undefined;

  const displayName = normalizingMeasureMeta
    ? `${baseMeasureMeta.fieldMeta.displayName} / ${normalizingMeasureMeta.fieldMeta.displayName}`
    : baseMeasureMeta.fieldMeta.displayName;

  return {
    ...baseMeasureMeta,
    fieldMeta: {
      ...(normalizingMeasureMeta ? undefined : baseMeasureMeta.fieldMeta),
      type: 'number',
      displayName,
      description: getNormalizedMeasureDescription(
        rawNormalizedMeasure,
        biMetadata
      ),
      //TODO(ben): Add unit and possibly tweak formatting to be smarter
      fieldId: computeNormalizedMeasureRawField(rawNormalizedMeasure),
      shouldFormatToPercent: isNormalizedMeasureIsPercent(rawNormalizedMeasure),
      emptyStringType: baseMeasureMeta.fieldMeta.emptyStringType,
    },
  };
}

function isNormalizedMeasureIsPercent(
  normalizedMeasure: BiDynamicNormalizedMeasure
) {
  return (
    normalizedMeasure.baseMeasure ===
    normalizedMeasure.normalizingQuery?.measure
  );
}

type BiDynamicNormalizedMeasureWithNormalizingQuery = RequireSomeProperties<
  BiDynamicNormalizedMeasure,
  'normalizingQuery'
>;
function isTrueNormalizedMeasure(
  measure: BiDynamicNormalizedMeasure
): measure is BiDynamicNormalizedMeasureWithNormalizingQuery {
  return measure.normalizingQuery != null;
}

function getPercentageMeasureDescription(
  {
    normalizingQuery,
  }: RequireSomeProperties<BiDynamicNormalizedMeasure, 'normalizingQuery'>,
  options: { percentString?: string } = { percentString: '%' }
): string {
  return `${options.percentString} ${
    // there's a logically possible case here where filters are present but not the whole numerator.
    // this case would still be roughly a "percent" of numerator but it might be better classified as an intensity
    // not sure if we will ever have this case or if we do the user will likely name this themselves but calling it out for thoroughness
    ('filters' in normalizingQuery && normalizingQuery.filters.length > 0) ||
    ('computedFiltersType' in normalizingQuery &&
      normalizingQuery.computedFiltersType === 'NumeratorIntersection')
      ? 'of view'
      : 'of total'
  }`;
}

export function getNormalizedMeasureDescription(
  normalizedMeasure: BiDynamicNormalizedMeasure,
  biMetadata: BiMetadata,
  options: { perString?: string; percentString?: string } = {
    perString: 'per',
    percentString: '%',
  }
): string | undefined {
  const { baseMeasure } = normalizedMeasure;
  const baseMeasureMeta = findMeasureMeta(baseMeasure, biMetadata);
  if (!isTrueNormalizedMeasure(normalizedMeasure)) {
    return baseMeasureMeta.fieldMeta.description;
  }

  const { normalizingQuery } = normalizedMeasure;
  if (isNormalizedMeasureIsPercent(normalizedMeasure)) {
    return getPercentageMeasureDescription(normalizedMeasure, options);
  }
  const normalizingMeasureMeta = findMeasureMeta(
    normalizingQuery.measure,
    biMetadata
  );
  const baseMeasureUnit = getUserReadableUnitFromMeasureMeta(baseMeasureMeta);
  const normalizingMeasureUnit = getUserReadableUnitFromMeasureMeta(
    normalizingMeasureMeta
  );
  return baseMeasureUnit && normalizingMeasureUnit
    ? `${baseMeasureUnit} ${options?.perString} ${normalizingMeasureUnit}`
    : undefined;
}

function getUserReadableUnitFromMeasureMeta(
  measure: BiMeasureMeta
): string | undefined {
  return getUserReadableUnitFromFieldMeta(measure.fieldMeta);
}

function getUserReadableUnitFromFieldMeta(
  fieldMeta: BiNumberFieldMeta
): string | undefined {
  const readableUnit = getUserReadableUnitFromFieldMetaIfPossible(fieldMeta);
  if (readableUnit === undefined) {
    // Record a Sentry error but not throw a user-facing error
    captureException(
      new UnexpectedError(
        `Measure unit not defined for field id: ${fieldMeta.fieldId}.`
      )
    );
  }
  return readableUnit;
}

export function getUserReadableUnitFromFieldMetaIfPossible(
  fieldMeta: BiNumberFieldMeta
): string | undefined {
  if (fieldMeta.isCurrency) {
    return condenseMonetaryValue(fieldMeta.divideBy ?? 1, fieldMeta.unit);
  }
  if ('unitUserFacing' in fieldMeta && !!fieldMeta.unitUserFacing) {
    return fieldMeta.unitUserFacing;
  }
  if (!!fieldMeta.unit) {
    return getFormattedUnitStringOrNull(fieldMeta.unit) ?? fieldMeta.unit;
  }
  return undefined;
}

export function getBiMeasureSelectOptionDisplayName(
  normalizedMeasure: BiDynamicNormalizedMeasureWithMeta,
  biMetadata: BiMetadata
): string {
  if (!isTrueNormalizedMeasure(normalizedMeasure)) {
    return (
      getBiFieldNameDescriptionString({
        aggregateType: normalizedMeasure.measureMeta.fieldMeta.aggregateType,
      }) ?? 'Absolute'
    );
  }

  if (normalizedMeasure.measureMeta.fieldMeta.shouldFormatToPercent) {
    return getPercentageMeasureDescription(normalizedMeasure, {
      percentString: 'Percent',
    });
  }

  const normalizingMeasureMeta = findMeasureMeta(
    normalizedMeasure.normalizingQuery.measure,
    biMetadata
  );

  return `${normalizingMeasureMeta.fieldMeta.displayName} intensity`;
}
export function findDimensionMeta(
  fieldId: string,
  biMetadata: BiMetadata
): BiMetadata['dimensions'][number];
export function findDimensionMeta(
  fieldId: string,
  biMetadata: BiMetadata,
  useSoftMust: true
): BiMetadata['dimensions'][number] | undefined;
export function findDimensionMeta(
  fieldId: string,
  biMetadata: BiMetadata,
  useSoftMust?: true
): BiMetadata['dimensions'][number] | undefined {
  const dimension = biMetadata.dimensions.find(
    (d) => d.fieldMeta.fieldId === fieldId
  );
  if (!dimension) {
    const error = new UnexpectedError(`Metadata not found for dimension`, {
      data: { fieldId },
    });
    if (useSoftMust) {
      captureException(error, { level: 'warning' });
    } else {
      throw error;
    }
  }
  return dimension;
}

export function findMeasureMeta(
  fieldId: string,
  biMetadata: BiMetadata
): BiMetadata['measures'][number];
export function findMeasureMeta(
  fieldId: string,
  biMetadata: BiMetadata,
  useSoftMust: true
): BiMetadata['measures'][number] | undefined;
export function findMeasureMeta(
  fieldId: string,
  biMetadata: BiMetadata,
  useSoftMust?: true
): BiMetadata['measures'][number] | undefined {
  const measure = biMetadata.measures.find(
    (m) => m.fieldMeta.fieldId === fieldId
  );
  if (!measure) {
    const error = new UnexpectedError(`Metadata not found for measure`, {
      data: { fieldId },
    });
    if (useSoftMust) {
      captureException(error, { level: 'warning' });
    } else {
      throw error;
    }
  }
  return measure;
}

export function getGroupingDimensionsFromQuery(
  query: BiQueryComponents
): Array<string> {
  // for now this is exactly the dimensions but there could be worlds where these become columns instead of groups
  return query.dimensions;
}

/** Internal sentinel value when we fabricate an "Other" value for some dimension. */
export const OTHER_VALUE_SENTINEL = '__Other';

export function isOther(dimensionId: BiRawValueType): boolean {
  return dimensionId === OTHER_VALUE_SENTINEL;
}

export function originalLabelOrOther(dimensionValue: string): string {
  if (dimensionValue === OTHER_VALUE_SENTINEL) {
    return 'Other';
  }
  return dimensionValue;
}

function inlineExponentString({
  name,
  exponent,
}: {
  name: string;
  exponent: number;
}): string {
  switch (exponent) {
    case 1:
      return name;
    case 2:
      return `sq ${name}`;
    case 3:
      return `cubic ${name}`;
    default:
      const getSuperscriptDigit = (digit: number): string => {
        switch (digit) {
          case 0:
            return '⁰';
          case 1:
            return '¹';
          case 2:
            return '²';
          case 3:
            return '³';
          case 4:
            return '⁴';
          case 5:
            return '⁵';
          case 6:
            return '⁶';
          case 7:
            return '⁷';
          case 8:
            return '⁸';
          case 9:
            return '⁹';
          default:
            return '';
        }
      };
      // Convert the exponent to a string of superscript digits.
      // e.g. 10 -> ¹⁰
      const digits = exponent
        .toString()
        .split('')
        .map(Number)
        .map(getSuperscriptDigit)
        .join('');
      return `${name}${digits}`;
  }
}

export const getInlineExplanationUnit = (
  unitString: string,
  perString: string = 'per'
): string => {
  const parsedUnit = parseUnitString(unitString, { preserveName: true });

  // If a null alias got here (e.g. count, orders), just render it as-is.
  if (isNullUnit(parsedUnit)) {
    return unitString;
  }

  const reformattedUnit = reformatUnit(parsedUnit);
  const numerators: Array<string> = [];
  const denominators: Array<string> = [];
  for (const [name, exponent] of reformattedUnit) {
    if (exponent > 0) {
      numerators.push(inlineExponentString({ name, exponent }));
    } else if (exponent < 0) {
      denominators.push(
        inlineExponentString({ name, exponent: Math.abs(exponent) })
      );
    }
  }

  const numerator = numerators.join(' · ');
  const denominator = denominators.join(' · ');
  return `${numerator}${
    denominators.length > 0 ? ` ${perString} ${denominator}` : ''
  }`;
};

export function getBiFieldNameDescriptionString(
  {
    description,
    unit,
    aggregateType,
    shouldFormatToPercent,
  }: Pick<BiFieldMeta, 'description'> &
    Pick<
      BiMeasureFieldMeta,
      'aggregateType' | 'unit' | 'shouldFormatToPercent'
    >,
  options?: { perString?: string }
): string | undefined {
  const aggtypeString = aggregateType
    ? AGGREGATE_KIND_DISPLAY_VALUE[aggregateType]
    : null;
  const unitString = unit
    ? getInlineExplanationUnit(unit, options?.perString)
    : null;
  const percentString = shouldFormatToPercent ? '%' : null;
  const unityStrings = [aggtypeString, unitString, percentString].filter(
    isNotNullish
  );
  if (unityStrings.length > 0) {
    return unityStrings.join(', ');
  }
  return description;
}

export function getDisplayValueMappingsForDimension(
  dimension: string,
  displayValueMappings: Array<GQBiDisplayValueMapping>
): Array<GQBiDimensionListOptionItem> {
  return (
    displayValueMappings.find((mapping) => mapping.dimension === dimension)
      ?.listOptionItems ?? []
  );
}

/** Format a value for a given dimension */
export function dimensionValueFormatterWithField({
  value,
  dimension,
  biMetadata,
  displayValueMappings,
}: {
  value: BiFieldMetaToPrimitiveType<BiDimensionFieldMeta>;
  dimension: string;
  biMetadata: BiMetadata;
  displayValueMappings: Array<GQBiDisplayValueMapping>;
}): string {
  const { fieldMeta } = findDimensionMeta(dimension, biMetadata);

  return formatWithFieldMeta({
    calculatedValue: getCalculatedDimensionValueFromRaw({
      value,
      fieldMeta,
    }),
    fieldMeta: { ...fieldMeta, emptyStringType: GQBiEmptyStringType.Emdash },
    displayValueMappings: getDisplayValueMappingsForDimension(
      fieldMeta.fieldId,
      displayValueMappings
    ),
  });
}

export function biQueryFilterFromDrilldownFilter(
  filter: DrilldownFilterExpressionPrimitive
): BiQueryFilter {
  return {
    dimension: filter.field,
    operator: convertDrilldownFilterOperator(filter.operator),
    value: filter.value,
  };
}

function convertDrilldownFilterOperator(
  op: DrilldownFilterExpressionPrimitive['operator']
): BiQueryFilter['operator'] {
  switch (op) {
    case 'in':
      return 'In';
    case 'notIn':
      return 'NotIn';
    default:
      throw new Error('Unsupported operator: ' + op);
  }
}

export function rowValueToFilterValues(
  value: BiRawValueType
): BiQueryFilter['value'] {
  return Array.isArray(value) ? value : [value ?? null];
}

/**
 * A query result is considered empty if all measure values are 0 or null. Missing columns
 * are considered empty.
 */
export function isBiQueryResultEmpty<T extends Row>(
  result: Columnar<T>,
  queryComponents: BiQueryComponents
): boolean {
  const measureIds = queryComponents.normalizedMeasures.map(
    (field) => field.measureMeta.fieldMeta.fieldId
  );

  for (const key of measureIds) {
    const col = result[key];
    if (col) {
      const values = col.data ?? [];
      if (values.length > 0) {
        if (
          col.type === 'categorical' &&
          // We appear to use categorical/dictionary columns for numerical data in some(?) cases;
          // this check tests whether all of that data is zero/null
          !Array.from(col.categories).every((cat) => cat === 0 || cat === null)
        ) {
          // zeroes are valid data for categorical columns, just check nulls
          for (let i = 0; i < values.length; i++) {
            if (values[i] !== null) return false;
          }
        } else if (col.type === 'plain') {
          for (let i = 0; i < values.length; i++) {
            // For numeric columns, all zeroes also count as empty
            if (values[i] !== 0 && values[i] !== null) return false;
          }
        }
      }
    }
  }

  return true;
}
